/**
 * Showing Scheduler + public booking helpers (ZRF-SHOW-01, Calendly-style link).
 */

export function asArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.properties)) return payload.properties;
  if (Array.isArray(payload?.showings)) return payload.showings;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.slots)) return payload.slots;
  if (Array.isArray(payload?.availableSlots)) return payload.availableSlots;
  return [];
}

export function toDate(value) {
  if (!value) return null;
  try {
    if (typeof value.toDate === 'function') return value.toDate();
    if (typeof value._seconds === 'number') return new Date(value._seconds * 1000);
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

/** Read ?l= (landlord link) and ?propertyId= from the booking page URL. */
export function parseBookingSearch(search = '') {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return {
    bookingSlug: params.get('l') || '',
    propertyId: params.get('propertyId') || '',
  };
}

/**
 * Public booking URL for prospects.
 * Prefer the landlord link (?l=) so only that manager's properties appear.
 */
export function publicBookingUrl({ slug = '', propertyId = '' } = {}) {
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';
  const base = origin || '';
  const params = new URLSearchParams();
  if (slug) params.set('l', slug);
  if (propertyId) params.set('propertyId', propertyId);
  const qs = params.toString();
  return qs ? `${base}/book-showing?${qs}` : `${base || ''}/book-showing`;
}

/** Landlord booking link narrowed to one property (pre-selected on the public page). */
export function propertyBookingUrl(bookingUrl, propertyId) {
  if (!bookingUrl || !propertyId) return bookingUrl || '';
  try {
    const url = new URL(bookingUrl);
    url.searchParams.set('propertyId', propertyId);
    return url.toString();
  } catch {
    const sep = bookingUrl.includes('?') ? '&' : '?';
    return `${bookingUrl}${sep}propertyId=${encodeURIComponent(propertyId)}`;
  }
}

/**
 * Public rental application URL. A unit link opens that unit; a property link shows only that
 * property; otherwise the landlord link (?l=) lists all of the landlord's properties.
 * Returns '' until a slug or property is known.
 */
export function publicApplicationUrl({ slug = '', propertyId = '', unitId = '' } = {}) {
  const origin =
    typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
  if (propertyId && unitId) {
    return `${origin}/apply/${encodeURIComponent(propertyId)}/${encodeURIComponent(unitId)}`;
  }
  if (propertyId) return `${origin}/apply?propertyId=${encodeURIComponent(propertyId)}`;
  if (slug) return `${origin}/apply?l=${encodeURIComponent(slug)}`;
  return '';
}

/**
 * The part of an availability slot that falls on one local calendar day, using the same
 * rules as the booking page: recurring slots open daily from the start's clock time to the
 * end's clock time on their weekdays; one-off slots are one continuous stretch.
 * Returns { start, end, allDay } or null when nothing is open that day.
 */
export function availabilityWindowForDay(slot, day) {
  const slotStart = toDate(slot?.startTime);
  const slotEnd = toDate(slot?.endTime);
  if (!slotStart || !slotEnd || slotStart >= slotEnd) return null;

  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
  let start = dayStart;
  let end = dayEnd;

  if (slot.recurring && Array.isArray(slot.daysOfWeek)) {
    if (!slot.daysOfWeek.includes(dayStart.getDay())) return null;
    start = new Date(dayStart);
    start.setHours(slotStart.getHours(), slotStart.getMinutes(), 0, 0);
    end = new Date(dayStart);
    end.setHours(slotEnd.getHours(), slotEnd.getMinutes(), 0, 0);
  }

  if (slotStart > start) start = slotStart;
  if (slotEnd < end) end = slotEnd;
  if (start >= end) return null;
  return { start, end, allDay: start.getTime() === dayStart.getTime() && end.getTime() === dayEnd.getTime() };
}

/** "Name · Address" so landlord and prospect screens name a property the same way. */
export function propertyLabel(property) {
  const name = (property?.name || '').trim();
  const address = (property?.address || '').trim();
  if (name && address && name !== address) return `${name} · ${address}`;
  return name || address;
}

/** YYYY-MM-DD in the given IANA time zone (defaults to browser zone). */
export function dateKeyInTimeZone(iso, timeZone) {
  const d = toDate(iso);
  if (!d) return '';
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function formatDayLabel(dateKey, timeZone) {
  const [y, m, d] = dateKey.split('-').map(Number);
  const noon = new Date(Date.UTC(y, m - 1, d, 12));
  return noon.toLocaleDateString('en-CA', {
    timeZone: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

export function formatSlotTime(iso, timeZone) {
  const d = toDate(iso);
  if (!d) return '';
  return d.toLocaleTimeString('en-CA', {
    timeZone: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatSlotDateTime(iso, timeZone) {
  const d = toDate(iso);
  if (!d) return '';
  return d.toLocaleString('en-CA', {
    timeZone: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Group open slots by local calendar day. Returns sorted [{ dateKey, label, slots }]. */
export function groupSlotsByDay(slots, timeZone) {
  const map = new Map();
  for (const slot of asArray(slots)) {
    const key = dateKeyInTimeZone(slot.start, timeZone);
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, { dateKey: key, label: formatDayLabel(key, timeZone), slots: [] });
    }
    map.get(key).slots.push(slot);
  }
  return [...map.values()]
    .map((day) => ({
      ...day,
      slots: day.slots.sort(
        (a, b) => toDate(a.start).getTime() - toDate(b.start).getTime()
      ),
    }))
    .sort((a, b) => a.dateKey.localeCompare(b.dateKey));
}
